import { type ComponentType } from 'react';

import {
  BlocksIcon,
  BoxIcon,
  ClockIcon,
  MessagesSquareIcon,
  SettingsIcon,
  UsersIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import IfIcon from '@/components/atoms/Icons/IfIcon';
import { CommandGroup } from '@/components/ui/command';
import { selectAccountSlug } from '@/redux/session/selectors';

import { CommandLinkItem } from '../CommandLinkItem';
import { type SelectedDesk } from '../types';

type DeskSubPagesGroupProps = {
  desk: SelectedDesk;
  onSelect: (path: string, label: string) => void;
};

type SubPage = {
  i18nKey: string;
  suffix: string;
  icon: ComponentType<{ className?: string }>;
  serviceDeskOnly?: boolean;
  chatRoute?: boolean;
};

const SUB_PAGES: SubPage[] = [
  {
    i18nKey: 'common.conversations',
    suffix: '/conversations',
    icon: MessagesSquareIcon,
    serviceDeskOnly: true,
    chatRoute: true,
  },
  { i18nKey: 'common.integrations', suffix: '/integrations', icon: BlocksIcon },
  { i18nKey: 'common.rules', suffix: '/rules', icon: IfIcon },
  { i18nKey: 'common.context_bundles', suffix: '/context_bundles', icon: BoxIcon },
  { i18nKey: 'common.business_hours', suffix: '/business_hours', icon: ClockIcon },
  { i18nKey: 'common.departments', suffix: '/departments', icon: UsersIcon },
  { i18nKey: 'common.settings', suffix: '/settings', icon: SettingsIcon },
];

export const DeskSubPagesGroup = ({ desk, onSelect }: DeskSubPagesGroupProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);

  const pages = SUB_PAGES.filter((p) => !p.serviceDeskOnly || desk.is_service_desk);

  return (
    <CommandGroup heading={desk.name}>
      {pages.map((page) => {
        const label = t(page.i18nKey);
        const basePath = page.chatRoute
          ? `/chats/${desk.desk_id}`
          : `/environments/${desk.desk_id}`;
        const path = `${basePath}${page.suffix}`;
        const Icon = page.icon;
        return (
          <CommandLinkItem
            key={page.suffix}
            value={`desk-subpage-${page.suffix}`}
            href={slug ? `/${slug}${path}` : '#'}
            onSelect={() => onSelect(path, `${desk.name} > ${label}`)}
            keywords={[label]}
          >
            <Icon />
            <span>{label}</span>
          </CommandLinkItem>
        );
      })}
    </CommandGroup>
  );
};
